import React, { useState, useMemo } from "react";
import { IconButton, Text, ActionButton } from "@fluentui/react";
import { DismissRegular, ChevronDownRegular, ChevronUpRegular } from "@fluentui/react-icons";
import { CarListing } from "./types";
import styles from "./CarComparisonView.module.css";
import { capitalizeFirstLetter } from "../../utils/textUtils";

interface CarComparisonViewProps {
    cars: CarListing[];
    isOpen: boolean;
    onDismiss: () => void;
    onRemoveCar: (carId: string) => void;
    onAskAI?: (question: string) => void;
}

export const CarComparisonView: React.FC<CarComparisonViewProps> = ({
    cars,
    isOpen,
    onDismiss,
    onRemoveCar,
    onAskAI
}) => {
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

    const toggleSection = (section: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(section)) {
            newExpanded.delete(section);
        } else {
            newExpanded.add(section);
        }
        setExpandedSections(newExpanded);
    };

    // AI-driven labels for each car
    const carLabels = useMemo(() => {
        return cars.map(car => {
            const labels: string[] = [];

            // Best value
            if (car.precio_mercado_promedio && car.precio < car.precio_mercado_promedio * 0.85) {
                labels.push("MEJOR VALOR");
            }

            // Newest
            const newestYear = Math.max(...cars.map(c => c.fecha_fabricacion));
            if (car.fecha_fabricacion === newestYear) {
                labels.push("MÁS NUEVO");
            }

            // Lowest mileage
            const lowestKm = Math.min(...cars.map(c => c.kilometraje));
            if (car.kilometraje === lowestKm) {
                labels.push("MENOR KILOMETRAJE");
            }

            // Best transparency
            if (car.transparencia) {
                const maxTransparencia = Math.max(...cars.map(c => c.transparencia || 0));
                if (car.transparencia === maxTransparencia && car.transparencia >= 80) {
                    labels.push("VENDEDOR MÁS TRANSPARENTE");
                }
            }

            // Best deal (has discount)
            if (car.rebaja && car.rebaja > 10) {
                labels.push("MAYOR DESCUENTO");
            }

            return labels[0] || ""; // Return first label or empty
        });
    }, [cars]);

    // Determine winners for each category
    const winners = useMemo(() => {
        const w: { [key: string]: string } = {};

        if (cars.length === 0) {
            return w;
        }

        w.price = cars.reduce((min, car) => car.precio < min.precio ? car : min, cars[0]).id_unico;
        w.mileage = cars.reduce((min, car) => car.kilometraje < min.kilometraje ? car : min, cars[0]).id_unico;
        w.year = cars.reduce((max, car) => car.fecha_fabricacion > max.fecha_fabricacion ? car : max, cars[0]).id_unico;
        w.transparency = cars.reduce((max, car) => (car.transparencia || 0) > (max.transparencia || 0) ? car : max, cars[0]).id_unico;

        return w;
    }, [cars]);

    // Generate AI narrative
    const aiNarrative = useMemo(() => {
        if (cars.length < 2) return "";

        const narrativeParts: string[] = [];

        // Price comparison
        const prices = cars.map(c => c.precio);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        const priceDiff = maxPrice - minPrice;

        if (priceDiff > 1000) {
            const cheapestCar = cars.find(c => c.precio === minPrice)!;
            const expensiveCar = cars.find(c => c.precio === maxPrice)!;
            narrativeParts.push(
                `El ${cheapestCar.marca} ${cheapestCar.modelo} ${cheapestCar.fecha_fabricacion} es $${priceDiff.toLocaleString()} más económico que el ${expensiveCar.marca} ${expensiveCar.modelo} ${expensiveCar.fecha_fabricacion}.`
            );
        }

        // Mileage vs price insight
        const bestValueCar = cars.reduce((best, car) => {
            const valueScore = (car.precio_mercado_promedio || car.precio) / car.kilometraje;
            const bestScore = (best.precio_mercado_promedio || best.precio) / best.kilometraje;
            return valueScore < bestScore ? car : best;
        }, cars[0]);

        if (bestValueCar.precio_mercado_promedio) {
            const savings = bestValueCar.precio_mercado_promedio - bestValueCar.precio;
            if (savings > 1000) {
                narrativeParts.push(
                    `El ${bestValueCar.marca} ${bestValueCar.modelo} ofrece el mejor valor, con un precio $${Math.round(savings).toLocaleString()} por debajo del mercado.`
                );
            }
        }

        // Transparency insight
        const transparencyCars = cars.filter(c => c.transparencia);
        if (transparencyCars.length > 1) {
            const avgTransparency = transparencyCars.reduce((sum, c) => sum + (c.transparencia || 0), 0) / transparencyCars.length;
            const highTransparency = transparencyCars.filter(c => (c.transparencia || 0) > avgTransparency);

            if (highTransparency.length > 0) {
                const topCar = highTransparency[0];
                narrativeParts.push(
                    `El ${topCar.marca} ${topCar.modelo} tiene una puntuación de transparencia de ${topCar.transparencia}%, indicando una publicación más completa y confiable.`
                );
            }
        }

        return narrativeParts.join(" ");
    }, [cars]);

    if (!isOpen || cars.length === 0) return null;

    return (
        <div className={styles.overlay} onClick={onDismiss}>
            <div className={styles.comparisonContainer} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        <Text className={styles.headerTitle}>
                            Comparando {cars.length} vehículos
                        </Text>
                    </div>
                    <IconButton
                        iconProps={{ iconName: "Cancel" }}
                        onClick={onDismiss}
                        className={styles.closeButton}
                        ariaLabel="Cerrar comparación"
                    />
                </div>

                {/* LAYER 1: At a Glance Cards */}
                <div className={styles.glanceSection}>
                    <div className={styles.cardsGrid}>
                        {cars.map((car, index) => (
                            <div key={car.id_unico} className={styles.comparisonCard}>
                                <button
                                    className={styles.removeButton}
                                    onClick={() => onRemoveCar(car.id_unico)}
                                    aria-label="Remover del comparador"
                                >
                                    <DismissRegular />
                                </button>

                                {carLabels[index] && (
                                    <div className={styles.cardLabel}>
                                        {carLabels[index]}
                                    </div>
                                )}

                                <div className={styles.cardImage}>
                                    {car.imagen_url ? (
                                        <img src={car.imagen_url} alt={`${car.marca} ${car.modelo}`} />
                                    ) : (
                                        <div className={styles.imagePlaceholder}>
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <path d="M5 17l-2 2v-16a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H5z" stroke="currentColor" strokeWidth="1.5"/>
                                            </svg>
                                        </div>
                                    )}
                                </div>

                                <div className={styles.cardContent}>
                                    <Text className={styles.cardTitle}>
                                        {car.marca} {car.modelo}
                                    </Text>
                                    <Text className={styles.cardSubtitle}>
                                        {car.fecha_fabricacion}
                                        {car.version && ` • ${car.version}`}
                                    </Text>

                                    <div className={styles.cardMetrics}>
                                        <div className={styles.metric}>
                                            <Text className={styles.metricLabel}>Precio</Text>
                                            <Text className={styles.metricValue}>
                                                ${car.precio.toLocaleString()}
                                                {winners.price === car.id_unico && <span className={styles.winner}>✓</span>}
                                            </Text>
                                        </div>

                                        <div className={styles.metric}>
                                            <Text className={styles.metricLabel}>Kilometraje</Text>
                                            <Text className={styles.metricValue}>
                                                {car.kilometraje.toLocaleString()} km
                                                {winners.mileage === car.id_unico && <span className={styles.winner}>✓</span>}
                                            </Text>
                                        </div>

                                        {car.transparencia && (
                                            <div className={styles.metric}>
                                                <Text className={styles.metricLabel}>Transparencia</Text>
                                                <Text className={styles.metricValue}>
                                                    {car.transparencia}% 🔍
                                                    {winners.transparency === car.id_unico && <span className={styles.winner}>✓</span>}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* LAYER 2: AI Story Narration */}
                {aiNarrative && (
                    <div className={styles.narrativeSection}>
                        <div className={styles.narrativeHeader}>
                            <span className={styles.narrativeIcon}>💬</span>
                            <Text className={styles.narrativeTitle}>Análisis comparativo</Text>
                        </div>
                        <Text className={styles.narrativeText}>
                            {aiNarrative}
                        </Text>
                        {onAskAI && (
                            <div className={styles.narrativeActions}>
                                <ActionButton
                                    className={styles.askButton}
                                    onClick={() => onAskAI(`¿Cuál de estos ${cars.length} vehículos me recomiendas y por qué?`)}
                                >
                                    ¿Cuál me recomiendas?
                                </ActionButton>
                                <ActionButton
                                    className={styles.askButton}
                                    onClick={() => onAskAI(`Explícame las diferencias más importantes entre estos ${cars.length} vehículos`)}
                                >
                                    Explícame más
                                </ActionButton>
                            </div>
                        )}
                    </div>
                )}

                {/* LAYER 3: Detailed Specifications */}
                <div className={styles.detailsSection}>
                    {/* Lo Fundamental */}
                    <div className={styles.specCategory}>
                        <button
                            className={styles.categoryHeader}
                            onClick={() => toggleSection("fundamental")}
                        >
                            <Text className={styles.categoryTitle}>Lo Fundamental</Text>
                            {expandedSections.has("fundamental") ? <ChevronUpRegular /> : <ChevronDownRegular />}
                        </button>
                        {expandedSections.has("fundamental") && (
                            <div className={styles.specTable}>
                                {renderSpecRow("Precio", cars.map(c => `$${c.precio.toLocaleString()}`), winners.price)}
                                {renderSpecRow("Precio de mercado", cars.map(c =>
                                    c.precio_mercado_promedio ? `$${c.precio_mercado_promedio.toLocaleString()}` : "N/A"
                                ))}
                                {renderSpecRow("Diferencia vs mercado", cars.map(c => {
                                    if (!c.precio_mercado_promedio) return "N/A";
                                    const diff = c.precio - c.precio_mercado_promedio;
                                    return diff < 0
                                        ? `$${Math.abs(diff).toLocaleString()} debajo`
                                        : `$${diff.toLocaleString()} encima`;
                                }))}
                                {renderSpecRow("Transparencia", cars.map(c =>
                                    c.transparencia ? `${c.transparencia}%` : "N/A"
                                ), winners.transparency)}
                                {renderSpecRow("Descuento", cars.map(c =>
                                    c.rebaja ? `-${c.rebaja}%` : "N/A"
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Condición del Vehículo */}
                    <div className={styles.specCategory}>
                        <button
                            className={styles.categoryHeader}
                            onClick={() => toggleSection("condition")}
                        >
                            <Text className={styles.categoryTitle}>Condición del Vehículo</Text>
                            {expandedSections.has("condition") ? <ChevronUpRegular /> : <ChevronDownRegular />}
                        </button>
                        {expandedSections.has("condition") && (
                            <div className={styles.specTable}>
                                {renderSpecRow("Año", cars.map(c => c.fecha_fabricacion.toString()), winners.year)}
                                {renderSpecRow("Kilometraje", cars.map(c => `${c.kilometraje.toLocaleString()} km`), winners.mileage)}
                                {renderSpecRow("Único dueño", cars.map(c => c.unico_dueno || "No especificado"))}
                                {renderSpecRow("Mantenimiento", cars.map(c =>
                                    capitalizeFirstLetter(c.mantenimiento_status || c.mantenimiento || "No especificado")
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Características */}
                    <div className={styles.specCategory}>
                        <button
                            className={styles.categoryHeader}
                            onClick={() => toggleSection("features")}
                        >
                            <Text className={styles.categoryTitle}>Características</Text>
                            {expandedSections.has("features") ? <ChevronUpRegular /> : <ChevronDownRegular />}
                        </button>
                        {expandedSections.has("features") && (
                            <div className={styles.specTable}>
                                {renderSpecRow("Transmisión", cars.map(c => capitalizeFirstLetter(c.transmision)))}
                                {renderSpecRow("Combustible", cars.map(c => capitalizeFirstLetter(c.tipo_combustible)))}
                                {renderSpecRow("Tracción", cars.map(c => c.tipo_traccion || "No especificado"))}
                                {renderSpecRow("Motor", cars.map(c => c.motor || "No especificado"))}
                                {renderSpecRow("Color", cars.map(c => capitalizeFirstLetter(c.color || "No especificado")))}
                                {renderSpecRow("Asientos de cuero", cars.map(c => c.asientos_cuero || "No especificado"))}
                            </div>
                        )}
                    </div>

                    {/* El Vendedor */}
                    <div className={styles.specCategory}>
                        <button
                            className={styles.categoryHeader}
                            onClick={() => toggleSection("seller")}
                        >
                            <Text className={styles.categoryTitle}>El Vendedor</Text>
                            {expandedSections.has("seller") ? <ChevronUpRegular /> : <ChevronDownRegular />}
                        </button>
                        {expandedSections.has("seller") && (
                            <div className={styles.specTable}>
                                {renderSpecRow("Tipo de vendedor", cars.map(c =>
                                    capitalizeFirstLetter(c.tipo_vendedor || "No especificado")
                                ))}
                                {renderSpecRow("Ubicación", cars.map(c =>
                                    capitalizeFirstLetter(c.departamento || "No especificado")
                                ))}
                                {renderSpecRow("Vendedor", cars.map(c => c.vendedor_nombre || "No especificado"))}
                            </div>
                        )}
                    </div>

                    {/* Financiamiento & Negociación */}
                    <div className={styles.specCategory}>
                        <button
                            className={styles.categoryHeader}
                            onClick={() => toggleSection("financing")}
                        >
                            <Text className={styles.categoryTitle}>Financiamiento & Negociación</Text>
                            {expandedSections.has("financing") ? <ChevronUpRegular /> : <ChevronDownRegular />}
                        </button>
                        {expandedSections.has("financing") && (
                            <div className={styles.specTable}>
                                {renderSpecRow("Negociable", cars.map(c =>
                                    capitalizeFirstLetter(c.negociable || "No especificado")
                                ))}
                                {renderSpecRow("Financiamiento", cars.map(c =>
                                    c.financiamiento_detalle || c.financiamiento || "No especificado"
                                ))}
                                {renderSpecRow("Acepta cambios", cars.map(c =>
                                    capitalizeFirstLetter(c.acepta_cambios || "No especificado")
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    function renderSpecRow(label: string, values: string[], winnerId?: string) {
        return (
            <div className={styles.specRow}>
                <div className={styles.specLabel}>{label}</div>
                {values.map((value, idx) => (
                    <div
                        key={idx}
                        className={`${styles.specValue} ${winnerId === cars[idx].id_unico ? styles.winnerValue : ""}`}
                    >
                        {value}
                        {winnerId === cars[idx].id_unico && <span className={styles.winnerCheck}>✓</span>}
                    </div>
                ))}
            </div>
        );
    }
};
